import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    Trash2,
    AlertTriangle,
    Download,
    Pencil,
    X as XIcon,
    Save,
    RefreshCw,
    Plus,
    Info
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api, fmtDate, fmtDateTime, fmtMoney, fmtQty } from '../lib/api';
import { PriceDeltaBadge, OldNewPricePills } from '../components/PriceDeltaBadge';

function newRow() {
    return { name: '', quantity: 1, unit_price: 0, total: 0 };
}

export default function BillDetailPage() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const nav = useNavigate();
    const [bill, setBill] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    // edit mode
    const [editing, setEditing] = useState(false);
    // Bill-nickname inline editor (Phase 18)
    const [renaming, setRenaming] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [savingTitle, setSavingTitle] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState(null);
    // 2026-07: quick-fix tax dialog. When the amber "auto-reconstructed" or
    // red "unaccounted" banner is showing, users can pop this to enter the
    // correct CGST/SGST/IGST in seconds — without going through full edit mode.
    const [fixTaxOpen, setFixTaxOpen] = useState(false);
    const [fixTaxDraft, setFixTaxDraft] = useState({ cgst: '', sgst: '', igst: '' });
    const [fixTaxSaving, setFixTaxSaving] = useState(false);
    // Sprint 2026-07: rescan (re-OCR) state.
    const [rescanning, setRescanning] = useState(false);
    // Sprint 2026-07: compact vs detailed items table. Persisted per-user in
    // localStorage so the choice sticks across bills. Detailed mode reveals
    // HSN, MRP, Basic Rate, Discount and GST% — the extra columns power
    // users need to sanity-check the OCR extraction.
    const [detailedView, setDetailedView] = useState(() => {
        try { return localStorage.getItem('emb.bill.detailedView') === '1'; } catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem('emb.bill.detailedView', detailedView ? '1' : '0'); } catch { /* ignore */ }
    }, [detailedView]);
    // Sprint 2026-07: same as fix-tax but for a missed global discount line.
    // Users often show us bills where OCR grabbed items + total but missed
    // the "Less: Discount ₹X" or "Cash Disc 5%" line printed below the
    // items table — leaving a phantom gap that we mislabel as tax.
    const [fixDiscountOpen, setFixDiscountOpen] = useState(false);
    const [fixDiscountDraft, setFixDiscountDraft] = useState({ amount: '', percent: '' });
    const [fixDiscountSaving, setFixDiscountSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.getBill(id);
            setBill(r.bill);
            setAlerts(r.alerts || []);
        } catch (e) {
            toast.error('Failed to load bill');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load(); // eslint-disable-next-line
    }, [id]);

    async function onDelete() {
        if (!window.confirm('Delete this bill and its price alerts?')) return;
        await api.deleteBill(id);
        toast.success('Bill deleted');
        nav('/bills');
    }

    async function onRescan() {
        // Sprint 2026-07: re-run OCR extraction on the stored file. Warns
        // the user first because a rescan overwrites the extracted fields
        // (any manual line-item edits made through the edit mode are lost).
        if (!window.confirm(
            'Rescan will re-read this bill with the latest OCR (AI). '
            + 'Any manual edits to items or totals will be OVERWRITTEN. Continue?'
        )) return;
        setRescanning(true);
        try {
            const res = await api.rescanBill(id);
            setBill(res?.bill || res);
            toast.success('Bill rescanned. Please review the new values.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Rescan failed. Please try again.');
        } finally {
            setRescanning(false);
        }
    }

    async function onDownload() {
        setDownloading(true);
        try {
            await api.downloadBillFile(id, bill?.original_filename || 'bill');
            toast.success('Downloaded original');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Download failed');
        } finally {
            setDownloading(false);
        }
    }

    function beginEdit() {
        setDraft({
            vendor: bill.vendor || '',
            bill_date: bill.bill_date || '',
            invoice_number: bill.invoice_number || '',
            currency: bill.currency || '',
            subtotal: bill.subtotal ?? '',
            tax: bill.tax ?? '',
            total: bill.total ?? '',
            items: (bill.items || []).map((it) => ({
                name: it.name || '',
                quantity: it.quantity ?? 1,
                unit_price: it.unit_price ?? 0,
                total: it.total ?? 0,
            })),
        });
        setEditing(true);
    }

    function cancelEdit() {
        setDraft(null);
        setEditing(false);
    }

    function updItem(idx, patch) {
        setDraft((d) => {
            const items = [...d.items];
            const row = { ...items[idx], ...patch };
            // auto-recompute total if unit_price or quantity changed and total not explicitly edited
            if ('quantity' in patch || 'unit_price' in patch) {
                const q = Number(row.quantity) || 0;
                const p = Number(row.unit_price) || 0;
                row.total = Number((q * p).toFixed(2));
            }
            items[idx] = row;
            return { ...d, items };
        });
    }

    function addItem() {
        setDraft((d) => ({ ...d, items: [...d.items, newRow()] }));
    }
    function removeItem(idx) {
        setDraft((d) => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));
    }

    async function saveTitle() {
        const t = (titleDraft || '').trim();
        setSavingTitle(true);
        try {
            const r = await api.updateBill(id, { title: t || null });
            const updated = r?.bill || r;
            setBill(updated);
            setRenaming(false);
            toast.success(t ? 'Bill renamed' : 'Nickname cleared');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to rename bill');
        } finally {
            setSavingTitle(false);
        }
    }

    async function save() {
        if (!draft) return;
        // basic validation
        for (const [i, it] of draft.items.entries()) {
            if (!(it.name || '').trim()) {
                toast.error(`Row ${i + 1}: item name is required`);
                return;
            }
            if (Number.isNaN(Number(it.quantity)) || Number(it.quantity) <= 0) {
                toast.error(`Row ${i + 1}: quantity must be > 0`);
                return;
            }
            if (Number.isNaN(Number(it.unit_price))) {
                toast.error(`Row ${i + 1}: unit price must be a number`);
                return;
            }
        }
        setSaving(true);
        try {
            const body = {
                vendor: draft.vendor.trim() || null,
                bill_date: draft.bill_date || null,
                invoice_number: draft.invoice_number || null,
                currency: draft.currency || null,
                subtotal: draft.subtotal === '' || draft.subtotal === null ? null : Number(draft.subtotal),
                tax: draft.tax === '' || draft.tax === null ? null : Number(draft.tax),
                total: draft.total === '' || draft.total === null ? null : Number(draft.total),
                items: draft.items.map((it) => ({
                    name: it.name.trim(),
                    quantity: Number(it.quantity) || 1,
                    unit_price: Number(it.unit_price) || 0,
                    total:
                        it.total === '' || it.total === null || Number.isNaN(Number(it.total))
                            ? Number(((Number(it.quantity) || 1) * (Number(it.unit_price) || 0)).toFixed(2))
                            : Number(it.total),
                })),
            };
            const r = await api.updateBill(id, body);
            const updated = r?.bill || r;
            const freshAlerts = r?.alerts ?? [];
            setBill(updated);
            setAlerts(freshAlerts);
            setEditing(false);
            setDraft(null);
            toast.success('Bill updated. Price alerts recomputed.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to update bill');
        } finally {
            setSaving(false);
        }
    }

    // ---- Quick "Fix tax" flow (invoked from the amber / red banner) ----
    function openFixTax() {
        if (!bill) return;
        // Seed with current values so the user only edits what's wrong.
        setFixTaxDraft({
            cgst: bill.cgst_amount != null ? String(bill.cgst_amount) : '',
            sgst: bill.sgst_amount != null ? String(bill.sgst_amount) : '',
            igst: bill.igst_amount != null ? String(bill.igst_amount) : '',
        });
        setFixTaxOpen(true);
    }

    async function saveFixTax() {
        const cgst = fixTaxDraft.cgst === '' ? null : Number(fixTaxDraft.cgst);
        const sgst = fixTaxDraft.sgst === '' ? null : Number(fixTaxDraft.sgst);
        const igst = fixTaxDraft.igst === '' ? null : Number(fixTaxDraft.igst);
        if ([cgst, sgst, igst].some((v) => v != null && (Number.isNaN(v) || v < 0))) {
            toast.error('Tax amounts must be zero or positive numbers.');
            return;
        }
        // Sanity: don't allow CGST/SGST AND IGST both set — they're mutually
        // exclusive per Indian GST law (intra-state OR inter-state, not both).
        const hasIntra = (cgst || 0) > 0 || (sgst || 0) > 0;
        const hasInter = (igst || 0) > 0;
        if (hasIntra && hasInter) {
            toast.error('A bill is either intra-state (CGST+SGST) OR inter-state (IGST) — not both.');
            return;
        }
        const totalTax = (cgst || 0) + (sgst || 0) + (igst || 0);
        setFixTaxSaving(true);
        try {
            // Clear the auto-reconstruction flags so the amber banner disappears
            // once the user has confirmed the numbers by hand.
            const body = {
                cgst_amount: cgst,
                sgst_amount: sgst,
                igst_amount: igst,
                tax: totalTax || null,
                tax_reconstructed: false,
                tax_reconstruction_reason: null,
            };
            const r = await api.updateBill(id, body);
            setBill(r?.bill || r);
            setFixTaxOpen(false);
            toast.success('Tax updated. Round-off will recompute.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save tax');
        } finally {
            setFixTaxSaving(false);
        }
    }

    // ---- Quick "Fix discount" flow (twin of Fix tax for missed discount) ----
    function openFixDiscount() {
        if (!bill) return;
        setFixDiscountDraft({
            amount: bill.global_discount_amount != null ? String(bill.global_discount_amount) : '',
            percent: bill.global_discount_percent != null ? String(bill.global_discount_percent) : '',
        });
        setFixDiscountOpen(true);
    }

    async function saveFixDiscount() {
        const amt = fixDiscountDraft.amount === '' ? null : Number(fixDiscountDraft.amount);
        const pct = fixDiscountDraft.percent === '' ? null : Number(fixDiscountDraft.percent);
        if ([amt, pct].some((v) => v != null && (Number.isNaN(v) || v < 0))) {
            toast.error('Discount must be a positive number.');
            return;
        }
        // Mutually exclusive: user should provide either % or amount, not both.
        // If both are given, keep the ₹ amount and null out the % (the ₹ is
        // more precise and the backend will re-render either way).
        setFixDiscountSaving(true);
        try {
            const body = {
                global_discount_amount: amt,
                global_discount_percent: amt != null ? null : pct,
                // Also clear tax reconstruction flags — the "gap" is now
                // explained by the discount, not by missing tax.
                tax_reconstructed: false,
                tax_reconstruction_reason: null,
            };
            const r = await api.updateBill(id, body);
            setBill(r?.bill || r);
            setFixDiscountOpen(false);
            toast.success('Discount saved. Totals will recompute.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save discount');
        } finally {
            setFixDiscountSaving(false);
        }
    }

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-8 w-40" />
                <Skeleton className="h-40" />
                <Skeleton className="h-64" />
            </div>
        );
    }
    if (!bill) return <div>Bill not found.</div>;

    const canDownload = !!bill.file_id;
    const items = editing ? draft.items : bill.items || [];
    const verifyMode = (searchParams.get('verify') || '') === '1';

    // ---------- 2026-07: bill-level discount reconciliation ----------
    // For invoices with a bill-level "Less: Discount X%" line, the OCR
    // sometimes captures `global_discount_percent`/`_amount` and sometimes
    // misses them (in which case only the printed grand total reflects the
    // discount). Either way, we want the UI to show a self-consistent picture:
    // adjusted per-unit price, adjusted line total, and a Discount row in the
    // summary so `Subtotal + Tax − Discount + Round-off = Total`.
    //
    // `billDiscount.ratio` is the fraction to subtract from an incl-GST amount
    // (so `gross × (1 − ratio) = post-discount`). It's zero when there's no
    // discount to apply.
    const billDiscount = (() => {
        if (editing) return { ratio: 0, amount: 0, implicit: false, gdp: 0 };
        const gdp = Number(bill.global_discount_percent || 0);
        const gda = Number(bill.global_discount_amount || 0);
        const subtotal = Number(bill.subtotal || 0);
        const tax = Number(bill.tax || 0);
        const total = Number(bill.total || 0);
        const grossBase = subtotal + tax;

        let amount = 0;
        let implicit = false;
        if (gda > 0) amount = gda;
        else if (gdp > 0 && subtotal > 0) amount = subtotal * (gdp / 100);

        // Backend already applied captured discount to `effective_unit_price`.
        // We ONLY need to re-apply on the frontend when the discount is
        // implicit (extraction missed it) — otherwise we'd double-count.
        if (amount === 0 && grossBase - total > 1.0) {
            amount = grossBase - total;
            implicit = true;
        }

        const ratio = grossBase > 0 && amount > 0 && implicit
            ? amount / grossBase
            : 0;
        return { ratio, amount, implicit, gdp };
    })();

    return (
        <div className="space-y-5 animate-fade-in" data-testid="bill-detail-page">
            {verifyMode && !editing && (
                <div
                    className="rounded-md border border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] p-3 flex items-start gap-3"
                    data-testid="bill-verify-banner"
                >
                    <AlertTriangle size={18} className="mt-0.5 flex-none" />
                    <div className="flex-1">
                        <div className="font-semibold text-sm mb-0.5">Verify this extraction</div>
                        <div className="text-xs leading-relaxed opacity-90">
                            AI just read this bill. Please check the <strong>date</strong>, each <strong>item quantity</strong>,{' '}
                            each <strong>unit price</strong>, and any <strong>discount</strong>. Tap <strong>Edit</strong> above to correct
                            anything wrong before it becomes part of your ledger &amp; price-history.
                        </div>
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSearchParams({}); }}
                        data-testid="bill-verify-dismiss"
                    >
                        Looks correct
                    </Button>
                </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <Button variant="ghost" onClick={() => nav('/bills')} className="gap-2" data-testid="bill-detail-back">
                    <ArrowLeft size={16} /> Back to bills
                </Button>
                <div className="flex items-center gap-2">
                    {!editing ? (
                        <>
                            <Button
                                variant="outline"
                                onClick={beginEdit}
                                className="gap-2"
                                data-testid="bill-detail-edit"
                            >
                                <Pencil size={16} /> Edit
                            </Button>
                            <Button
                                variant="outline"
                                onClick={onDownload}
                                className="gap-2"
                                data-testid="bill-detail-download"
                                disabled={!canDownload || downloading}
                                title={canDownload ? 'Download the original file' : 'Original file not available for this bill'}
                            >
                                <Download size={16} /> {downloading ? 'Preparing…' : 'Download original'}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={onRescan}
                                className="gap-2"
                                data-testid="bill-detail-rescan"
                                disabled={!canDownload || rescanning}
                                title="Re-run OCR on this bill with the latest AI. Useful when the extraction picked wrong values."
                            >
                                <RefreshCw size={16} className={rescanning ? 'animate-spin' : ''} />
                                {rescanning ? 'Rescanning…' : 'Rescan (re-OCR)'}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={onDelete}
                                className="gap-2 text-[hsl(var(--price-increase-fg))] border-[hsl(var(--price-increase-border))] hover:bg-[hsl(var(--price-increase-bg))]"
                                data-testid="bill-detail-delete"
                            >
                                <Trash2 size={16} /> Delete bill
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={cancelEdit} className="gap-2" data-testid="bill-detail-cancel">
                                <XIcon size={16} /> Cancel
                            </Button>
                            <Button onClick={save} className="gap-2" disabled={saving} data-testid="bill-detail-save">
                                <Save size={16} /> {saving ? 'Saving…' : 'Save changes'}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {!editing && (bill.items || []).some((it) => it.reconciled) && (
                <Alert
                    className="border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))]"
                    data-testid="bill-detail-reconciled-hint"
                >
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle className="font-heading">Some values were auto-corrected</AlertTitle>
                    <AlertDescription>
                        For {(bill.items || []).filter((it) => it.reconciled).length} row(s), the extracted{' '}
                        quantity × unit price didn't add up to the row total, so Ease My Bill auto-corrected the field that
                        looked most likely mis-read. Please open <strong>Edit</strong> above and double-check them.
                    </AlertDescription>
                </Alert>
            )}

            {!editing && (
                <Alert className="border-accent bg-accent/50" data-testid="bill-detail-edit-hint">
                    <Info className="h-4 w-4" />
                    <AlertTitle className="font-heading">Something wrong with the extracted data?</AlertTitle>
                    <AlertDescription>
                        OCR isn't always perfect — click <strong>Edit</strong> above to fix any quantity, price or item
                        name. Price alerts will be recomputed automatically after you save.
                    </AlertDescription>
                </Alert>
            )}

            <Card className="p-5 border-border/80">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                        {!editing ? (
                            <>
                                {/* Bill nickname / title — inline editable (Phase 18) */}
                                {renaming ? (
                                    <div className="flex items-center gap-2 mb-2" data-testid="bill-title-editor">
                                        <Input
                                            autoFocus
                                            value={titleDraft}
                                            onChange={(e) => setTitleDraft(e.target.value)}
                                            placeholder="e.g. March rent invoice, Diwali order…"
                                            maxLength={80}
                                            className="max-w-md h-9"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveTitle();
                                                if (e.key === 'Escape') { setRenaming(false); setTitleDraft(bill.title || ''); }
                                            }}
                                            data-testid="bill-title-input"
                                        />
                                        <Button
                                            size="sm"
                                            onClick={saveTitle}
                                            disabled={savingTitle}
                                            data-testid="bill-title-save"
                                        >
                                            {savingTitle ? 'Saving…' : 'Save'}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => { setRenaming(false); setTitleDraft(bill.title || ''); }}
                                            data-testid="bill-title-cancel"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 mb-1" data-testid="bill-title-display">
                                        {bill.title ? (
                                            <div className="font-heading text-xl text-foreground" data-testid="bill-title-value">
                                                {bill.title}
                                            </div>
                                        ) : (
                                            <div className="text-xs uppercase tracking-wide text-muted-foreground">
                                                No nickname
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => { setTitleDraft(bill.title || ''); setRenaming(true); }}
                                            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                                            data-testid="bill-title-rename-btn"
                                            aria-label={bill.title ? 'Rename bill' : 'Add nickname'}
                                        >
                                            <Pencil size={12} /> {bill.title ? 'rename' : 'add nickname'}
                                        </button>
                                    </div>
                                )}
                                <div className={bill.title ? 'text-sm text-muted-foreground' : 'font-heading text-2xl'}>
                                    {bill.vendor_key ? (
                                        <Link to={`/vendors/${encodeURIComponent(bill.vendor_key)}`} className="hover:underline">
                                            {bill.vendor}
                                        </Link>
                                    ) : (
                                        bill.vendor
                                    )}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {bill.invoice_number ? `Invoice #${bill.invoice_number} · ` : ''}
                                    {fmtDate(bill.bill_date)}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    Uploaded {fmtDateTime(bill.uploaded_at)}
                                    {bill.original_filename && ` · ${bill.original_filename}`}
                                    {bill.extracted_by && (
                                        <span
                                            className="ml-2 inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] uppercase tracking-wide"
                                            data-testid="bill-extracted-by"
                                        >
                                            AI
                                        </span>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="sm:col-span-2">
                                    <Label>Vendor</Label>
                                    <Input
                                        data-testid="edit-vendor"
                                        value={draft.vendor}
                                        onChange={(e) => setDraft((d) => ({ ...d, vendor: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <Label>Invoice number</Label>
                                    <Input
                                        data-testid="edit-invoice"
                                        value={draft.invoice_number}
                                        onChange={(e) => setDraft((d) => ({ ...d, invoice_number: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <Label>Bill date</Label>
                                    <Input
                                        data-testid="edit-billdate"
                                        type="date"
                                        value={draft.bill_date || ''}
                                        onChange={(e) => setDraft((d) => ({ ...d, bill_date: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <Label>Currency</Label>
                                    <Input
                                        data-testid="edit-currency"
                                        placeholder="INR"
                                        value={draft.currency}
                                        onChange={(e) => setDraft((d) => ({ ...d, currency: e.target.value }))}
                                    />
                                </div>
                            </div>
                        )}

                        {!editing && alerts.length > 0 && (
                            <Alert
                                className="mt-4 border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))]"
                                data-testid="bill-detail-alerts"
                            >
                                <AlertTriangle className="h-4 w-4" />
                                <AlertTitle className="font-heading">{alerts.length} price change(s) on this bill</AlertTitle>
                                <AlertDescription>
                                    <ul className="mt-2 space-y-1">
                                        {alerts.map((a) => (
                                            <li key={a.id} className="flex flex-wrap items-center gap-2">
                                                <span className="font-medium">{a.item_name}</span>
                                                <OldNewPricePills
                                                    oldPrice={a.previous_price}
                                                    newPrice={a.new_price}
                                                    currency={bill.currency}
                                                />
                                                <PriceDeltaBadge delta={a.delta} deltaPercent={a.delta_percent} />
                                            </li>
                                        ))}
                                    </ul>
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="mt-5 overflow-x-auto rounded-lg border border-border">
                            {/* Sprint 2026-07: compact ↔ detailed view toggle. Detailed mode
                                surfaces the 8 fields the extractor targets on distributor bills
                                (Item · HSN · Qty · MRP · Basic Rate · Discount · GST% · Total)
                                so you can sanity-check the OCR extraction at a glance. */}
                            {!editing && (
                                <div className="flex justify-end px-3 py-1.5 border-b border-border/60 bg-muted/20">
                                    <button
                                        type="button"
                                        onClick={() => setDetailedView((v) => !v)}
                                        data-testid="bill-detail-view-toggle"
                                        className="text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
                                        title="Toggle between compact and detailed view"
                                    >
                                        {detailedView ? '‹ Compact view' : 'Detailed view ›'}
                                    </button>
                                </div>
                            )}
                            <table className="w-full text-sm" data-testid="bill-detail-line-items-table">
                                <thead className="bg-muted/40 text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-medium">Item</th>
                                        {!editing && detailedView && (
                                            <th className="text-left px-3 py-2 font-medium w-24">HSN</th>
                                        )}
                                        <th className="text-right px-3 py-2 font-medium w-20">Qty</th>
                                        {!editing && detailedView && (
                                            <th className="text-right px-3 py-2 font-medium w-24" title="Maximum Retail Price / list price BEFORE any discount">MRP</th>
                                        )}
                                        {!editing && detailedView && (
                                            <th className="text-right px-3 py-2 font-medium w-24" title="Distributor's per-unit sell rate BEFORE scheme/premium discount">Basic Rate</th>
                                        )}
                                        {!editing && detailedView && (
                                            <th className="text-right px-3 py-2 font-medium w-24" title="Total discount amount for this line (all Disc columns summed)">Disc ₹</th>
                                        )}
                                        {!editing && detailedView && (
                                            <th className="text-right px-3 py-2 font-medium w-16" title="GST rate">GST%</th>
                                        )}
                                        {(!detailedView || editing) && (
                                            <th className="text-right px-3 py-2 font-medium w-32">Unit price (incl. GST)</th>
                                        )}
                                        <th className="text-right px-3 py-2 font-medium w-32">Total</th>
                                        {editing && <th className="w-10" />}
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((it, idx) =>
                                        editing ? (
                                            <tr key={idx} className="border-t border-border" data-testid={`edit-row-${idx}`}>
                                                <td className="px-2 py-1.5">
                                                    <Input
                                                        data-testid={`edit-item-name-${idx}`}
                                                        value={it.name}
                                                        onChange={(e) => updItem(idx, { name: e.target.value })}
                                                        placeholder="Item name"
                                                        className="h-9"
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <Input
                                                        data-testid={`edit-item-qty-${idx}`}
                                                        type="number"
                                                        step="0.001"
                                                        min="0"
                                                        value={it.quantity}
                                                        onChange={(e) => updItem(idx, { quantity: e.target.value })}
                                                        className="h-9 text-right font-mono tabular-nums"
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <Input
                                                        data-testid={`edit-item-unitprice-${idx}`}
                                                        type="number"
                                                        step="0.01"
                                                        value={it.unit_price}
                                                        onChange={(e) => updItem(idx, { unit_price: e.target.value })}
                                                        className="h-9 text-right font-mono tabular-nums"
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <Input
                                                        data-testid={`edit-item-total-${idx}`}
                                                        type="number"
                                                        step="0.01"
                                                        value={it.total}
                                                        onChange={(e) => updItem(idx, { total: e.target.value })}
                                                        className="h-9 text-right font-mono tabular-nums"
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5 text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => removeItem(idx)}
                                                        data-testid={`edit-item-remove-${idx}`}
                                                        aria-label="Remove item"
                                                    >
                                                        <XIcon size={14} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr key={idx} className="border-t border-border hover:bg-muted/40">
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span>{it.name}</span>
                                                        {it.reconciled && (
                                                            <span
                                                                title="OCR math didn't add up; auto-corrected. Please verify this row."
                                                                data-testid={`item-reconciled-badge-${idx}`}
                                                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border border-[hsl(var(--warning-border))] rounded px-1.5 py-0.5"
                                                            >
                                                                auto-corrected
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                {detailedView && (
                                                    <td className="px-3 py-2 text-left tabular-nums font-mono text-xs text-muted-foreground" data-testid={`item-hsn-${idx}`}>
                                                        {it.hsn || <span className="opacity-40">—</span>}
                                                    </td>
                                                )}
                                                <td className="px-3 py-2 text-right tabular-nums font-mono">{fmtQty(it.quantity, { dash: '1' })}</td>
                                                {detailedView && (
                                                    <td className="px-3 py-2 text-right tabular-nums font-mono text-xs" data-testid={`item-mrp-${idx}`}>
                                                        {/* Post-swap: `mrp` holds retail sticker; fall back to legacy `basic_rate` field where old bills stored MRP. */}
                                                        {(it.mrp ?? it.basic_rate) != null ? fmtMoney(it.mrp ?? it.basic_rate, bill.currency) : <span className="opacity-40">—</span>}
                                                    </td>
                                                )}
                                                {detailedView && (
                                                    <td className="px-3 py-2 text-right tabular-nums font-mono text-xs" data-testid={`item-basic-rate-${idx}`}>
                                                        {/* Post-swap: `unit_price` now carries the wholesale Basic Rate (what the buyer pays). */}
                                                        {it.unit_price != null ? fmtMoney(it.unit_price, bill.currency) : <span className="opacity-40">—</span>}
                                                    </td>
                                                )}
                                                {detailedView && (
                                                    <td className="px-3 py-2 text-right tabular-nums font-mono text-xs" data-testid={`item-discount-${idx}`}>
                                                        {(() => {
                                                            const dAmt = Number(it.discount_amount) || 0;
                                                            const dPct = Number(it.discount_percent) || 0;
                                                            if (dAmt > 0) return (
                                                                <span className="text-[hsl(var(--price-decrease-fg))]">
                                                                    -{fmtMoney(dAmt, bill.currency)}
                                                                </span>
                                                            );
                                                            if (dPct > 0) return (
                                                                <span className="text-[hsl(var(--price-decrease-fg))]">{dPct}%</span>
                                                            );
                                                            return <span className="opacity-40">—</span>;
                                                        })()}
                                                    </td>
                                                )}
                                                {detailedView && (
                                                    <td className="px-3 py-2 text-right tabular-nums font-mono text-xs" data-testid={`item-tax-rate-${idx}`}>
                                                        {it.tax_rate != null ? `${it.tax_rate}%` : <span className="opacity-40">—</span>}
                                                    </td>
                                                )}
                                                {!detailedView && (
                                                <td className="px-3 py-2 text-right tabular-nums font-mono">
                                                    {(() => {
                                                        // 2026-07 (revised): the most reliable "unit price incl. GST"
                                                        // is simply `line_total ÷ qty` — this is the actual per-unit
                                                        // outflow, derived directly from what's printed on the bill.
                                                        // Only fall back to the computed `effective_unit_price` (which
                                                        // requires clean discount extraction) when we don't have a
                                                        // usable line total.
                                                        const qNum = Number(it.quantity || 0);
                                                        const tNum = Number(it.total || 0);
                                                        if (qNum > 0 && tNum > 0) {
                                                            const derived = tNum / qNum;
                                                            return fmtMoney(derived, bill.currency);
                                                        }
                                                        // Fallback path (no line total): the historical logic.
                                                        const rawUp = it.effective_unit_price != null ? it.effective_unit_price : it.unit_price;
                                                        if (rawUp == null || Number.isNaN(Number(rawUp))) {
                                                            return fmtMoney(rawUp, bill.currency);
                                                        }
                                                        const adjUp = billDiscount.implicit && billDiscount.ratio > 0
                                                            ? Number(rawUp) * (1 - billDiscount.ratio)
                                                            : Number(rawUp);
                                                        return fmtMoney(adjUp, bill.currency);
                                                    })()}
                                                    {/* "base ₹X" subtitle: raw wholesale rate (unit_price = Basic Rate). */}
                                                    {it.unit_price != null && Number(it.quantity || 0) > 0 && Number(it.total || 0) > 0 && (
                                                        <div
                                                            className="text-[10px] text-muted-foreground font-normal"
                                                            data-testid={`item-base-price-${idx}`}
                                                        >
                                                            base {fmtMoney(Number(it.unit_price), bill.currency)}
                                                        </div>
                                                    )}
                                                </td>
                                                )}
                                                <td className="px-3 py-2 text-right tabular-nums font-mono" data-testid={`item-total-${idx}`}>
                                                    {(() => {
                                                        // 2026-07 (revised): show the AUTHORITATIVE line total that was
                                                        // extracted from the invoice (`it.total`) — this is what's printed
                                                        // on the bill and what the buyer actually pays for that line. The
                                                        // per-line totals MUST sum to the bill's subtotal, so we can't
                                                        // synthesize a fake number here.
                                                        //
                                                        // Previously we computed `effective_unit_price × qty` to keep the
                                                        // Total column visually aligned with the Unit-price column. That
                                                        // was wrong: whenever discount extraction wasn't pixel-perfect
                                                        // (very common on distributor bills), the computed value diverged
                                                        // from the real total by hundreds of rupees, and the per-line
                                                        // totals no longer added up to the printed subtotal.
                                                        //
                                                        // We still surface the pre-discount "gross" as a small strike-through
                                                        // subtitle when a bill-level implicit discount is in effect, so the
                                                        // user can see how the discount was applied.
                                                        const upDisp = it.effective_unit_price != null ? it.effective_unit_price : it.unit_price;
                                                        const qty = it.quantity;
                                                        const authoritative = it.total;
                                                        // Fallback ONLY when the invoice didn't give us a line total.
                                                        if (authoritative == null || Number.isNaN(Number(authoritative))) {
                                                            if (upDisp == null || qty == null) return <span className="opacity-40">—</span>;
                                                            return fmtMoney(Number(upDisp) * Number(qty), bill.currency);
                                                        }
                                                        if (billDiscount.implicit && billDiscount.ratio > 0 && upDisp != null && qty != null) {
                                                            const gross = Number(upDisp) * Number(qty);
                                                            return (
                                                                <>
                                                                    <div>{fmtMoney(Number(authoritative), bill.currency)}</div>
                                                                    <div
                                                                        className="text-[10px] text-muted-foreground font-normal"
                                                                        data-testid={`item-gross-total-${idx}`}
                                                                        title={`Pre-discount ${fmtMoney(gross, bill.currency)}`}
                                                                    >
                                                                        <span className="line-through">{fmtMoney(gross, bill.currency)}</span>
                                                                    </div>
                                                                </>
                                                            );
                                                        }
                                                        return fmtMoney(Number(authoritative), bill.currency);
                                                    })()}
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {editing && (
                            <div className="mt-2 flex justify-between items-center">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={addItem}
                                    className="gap-2"
                                    data-testid="edit-add-item"
                                >
                                    <Plus size={14} /> Add item
                                </Button>
                                <div className="text-xs text-muted-foreground">
                                    Total auto-recomputes as you edit qty × unit price. You can override it manually.
                                </div>
                            </div>
                        )}

                        <Separator className="my-4" />
                        {!editing ? (
                            (() => {
                                // 2026-07: reuse the shared `billDiscount` computation so the
                                // summary and the item rows always tell the same story.
                                const subtotal = Number(bill.subtotal || 0);
                                const tax = Number(bill.tax || 0);
                                const total = Number(bill.total || 0);
                                const discountAmount = billDiscount.amount;
                                const discountLabel = billDiscount.gdp > 0
                                    ? `Discount (${billDiscount.gdp}%)`
                                    : 'Discount';
                                // Sprint 2026-07 fix: also compare against the ACTUAL sum of
                                // line-item totals. Handwritten wholesale bills often have OCR
                                // misread the subtotal field (it's usually crammed at the
                                // bottom in small type), but the per-item totals are read
                                // reliably. If sum-of-items already matches the grand total,
                                // there's no "unaccounted" gap — the OCR just misread the
                                // subtotal line. Old code compared against `subtotal` alone
                                // and generated a firestorm of false "Fix tax" banners.
                                const itemsSum = (bill.items || []).reduce(
                                    (a, it) => a + (Number(it.total) || 0), 0,
                                );
                                // Two candidate residuals — pick the smaller (in absolute
                                // magnitude) because that's the interpretation that fits
                                // best. If items already sum to the total, itemsResidual
                                // will be ~0 and we prefer that story.
                                const subtotalResidual = total - (subtotal + tax - discountAmount);
                                const itemsResidual = total - (itemsSum + tax - discountAmount);
                                const useItemsSum = itemsSum > 0 && Math.abs(itemsResidual) < Math.abs(subtotalResidual);
                                const residual = useItemsSum ? itemsResidual : subtotalResidual;
                                const showDiscount = discountAmount > 0.005;
                                const showRoundOff = Math.abs(residual) >= 0.01;
                                // Layer 3: guardrail — a residual > 5% of total OR > ₹5 is NOT round-off,
                                // it's almost certainly missed GST that the OCR didn't extract.
                                // Sprint 2026-07: also relaxed to only trigger when NEITHER
                                // interpretation is close to zero — otherwise it's just an
                                // OCR misread of the subtotal, not a genuine missing tax.
                                const suspiciousResidual =
                                    showRoundOff
                                    && (Math.abs(residual) > 5 || (total > 0 && Math.abs(residual) / total > 0.05))
                                    // If sum-of-items already reconciles with total (within
                                    // 2% or ₹5), the "gap" is just a bad subtotal read.
                                    && !(itemsSum > 0 && (Math.abs(itemsResidual) < 5 || (total > 0 && Math.abs(itemsResidual) / total < 0.02)));
                                const wasReconstructed = !!bill.tax_reconstructed;
                                const reconstructionNote = bill.tax_reconstruction_reason;
                                // Sprint 2026-07: detect the "OCR only read some items" case.
                                // If the printed subtotal is materially LARGER than sum-of-items
                                // AND at least 2 items were extracted, some items are almost
                                // certainly missing from the extraction. Prompt the user to
                                // re-scan or manually add the missing lines.
                                const missingItemsGap = (subtotal > 0 && itemsSum > 0)
                                    ? subtotal - itemsSum
                                    : 0;
                                const missingItemsSuspected =
                                    missingItemsGap > 50
                                    && missingItemsGap > subtotal * 0.10
                                    && (bill.items || []).length >= 1;

                                return (
                                    <div className="space-y-3" data-testid="bill-summary-wrap">
                                        {wasReconstructed && (
                                            <div className="rounded-md border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800/50 p-2.5 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2" data-testid="bill-tax-reconstructed-banner">
                                                <span className="text-amber-600 dark:text-amber-400 mt-0.5">⚠️</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium">GST amounts were auto-reconstructed</div>
                                                    <div className="text-[11px] opacity-90 mt-0.5">{reconstructionNote || 'OCR could not read the tax lines directly. The GST split was inferred from the subtotal-to-total gap. Please verify.'}</div>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="shrink-0 h-7 text-xs"
                                                    onClick={openFixTax}
                                                    data-testid="bill-fix-tax-btn-reconstructed"
                                                >
                                                    Fix tax
                                                </Button>
                                            </div>
                                        )}
                                        {!wasReconstructed && suspiciousResidual && (
                                            <div className="rounded-md border border-red-300 bg-red-50/60 dark:bg-red-950/20 dark:border-red-800/50 p-2.5 text-xs text-red-900 dark:text-red-200 flex items-start gap-2 flex-wrap" data-testid="bill-suspicious-residual-banner">
                                                <span className="text-red-600 dark:text-red-400 mt-0.5">⚠️</span>
                                                <div className="flex-1 min-w-[200px]">
                                                    <div className="font-medium">Unaccounted ₹{Math.abs(residual).toFixed(2)} between subtotal and total</div>
                                                    <div className="text-[11px] opacity-90 mt-0.5">
                                                        This gap is too large to be a real round-off (usually ≤ ₹1). It&apos;s likely{' '}
                                                        <span className="font-medium">
                                                            {residual > 0 ? 'a missed GST/tax line' : 'a missed discount line'}
                                                        </span>
                                                        {' '}the OCR couldn&apos;t read. Pick the right one:
                                                    </div>
                                                </div>
                                                <div className="flex gap-1 shrink-0">
                                                    <Button
                                                        size="sm"
                                                        variant={residual > 0 ? 'default' : 'outline'}
                                                        className="h-7 text-xs"
                                                        onClick={openFixTax}
                                                        data-testid="bill-fix-tax-btn-suspicious"
                                                    >
                                                        Fix tax
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant={residual < 0 ? 'default' : 'outline'}
                                                        className="h-7 text-xs"
                                                        onClick={openFixDiscount}
                                                        data-testid="bill-fix-discount-btn-suspicious"
                                                    >
                                                        Fix discount
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        {/* Sprint 2026-07: if items sum reconciles with total but
                                            the OCR-read subtotal doesn't, show a gentle heads-up.
                                            No alarming red banner — just an informational note. */}
                                        {useItemsSum && !suspiciousResidual && subtotal > 0 && Math.abs(itemsSum - subtotal) > 5 && (
                                            <div className="rounded-md border border-blue-300/60 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800/40 p-2 text-[11px] text-blue-900 dark:text-blue-200 flex items-start gap-2" data-testid="bill-subtotal-corrected-note">
                                                <Info size={12} className="mt-0.5 shrink-0 opacity-70" />
                                                <div>
                                                    Showing corrected subtotal from line-item totals ({fmtMoney(itemsSum, bill.currency)}). The OCR-read subtotal ({fmtMoney(subtotal, bill.currency)}) looked misread — items already reconcile with the grand total.
                                                </div>
                                            </div>
                                        )}
                                        {missingItemsSuspected && (
                                            <div className="rounded-md border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800/50 p-2.5 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2" data-testid="bill-missing-items-banner">
                                                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium">OCR may have missed ~{fmtMoney(missingItemsGap, bill.currency)} worth of items</div>
                                                    <div className="text-[11px] opacity-90 mt-0.5">
                                                        Only {(bill.items || []).length} items were read but the printed subtotal is ₹{subtotal.toFixed(2)}.
                                                        Please review the original bill and add any missing line items manually.
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm" data-testid="bill-summary-block">
                                        <div>
                                            <div className="text-muted-foreground text-xs">Subtotal{useItemsSum && Math.abs(itemsSum - subtotal) > 5 ? ' (corrected)' : ''}</div>
                                            <div className="tabular-nums font-mono" data-testid="bill-summary-subtotal">{fmtMoney(useItemsSum && Math.abs(itemsSum - subtotal) > 5 ? itemsSum : bill.subtotal, bill.currency)}</div>
                                        </div>
                                        <div>
                                            <div className="text-muted-foreground text-xs">Tax{wasReconstructed ? ' (auto)' : ''}</div>
                                            <div className="tabular-nums font-mono" data-testid="bill-summary-tax">{fmtMoney(bill.tax, bill.currency)}</div>
                                        </div>
                                        {showDiscount && (
                                            <div data-testid="bill-summary-discount-row">
                                                <div className="text-muted-foreground text-xs">{discountLabel}</div>
                                                <div
                                                    className="tabular-nums font-mono text-[hsl(var(--price-decrease-fg))]"
                                                    data-testid="bill-summary-discount"
                                                >
                                                    -{fmtMoney(discountAmount, bill.currency)}
                                                </div>
                                            </div>
                                        )}
                                        {showRoundOff && (
                                            <div data-testid="bill-summary-roundoff-row">
                                                <div className={`text-xs ${suspiciousResidual ? 'text-red-600 dark:text-red-400 font-medium' : 'text-muted-foreground'}`}>
                                                    {suspiciousResidual ? 'Unaccounted ⚠️' : 'Round-off'}
                                                </div>
                                                <div
                                                    className={`tabular-nums font-mono ${suspiciousResidual ? 'text-red-700 dark:text-red-400' : 'text-muted-foreground'}`}
                                                    data-testid="bill-summary-roundoff"
                                                >
                                                    {residual >= 0 ? '+' : ''}{fmtMoney(residual, bill.currency)}
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <div className="text-muted-foreground text-xs">Total</div>
                                            <div className="font-heading tabular-nums" data-testid="bill-summary-total">{fmtMoney(bill.total, bill.currency)}</div>
                                        </div>
                                        </div>
                                    </div>
                                );
                            })()
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <Label>Subtotal</Label>
                                    <Input
                                        data-testid="edit-subtotal"
                                        type="number"
                                        step="0.01"
                                        value={draft.subtotal}
                                        onChange={(e) => setDraft((d) => ({ ...d, subtotal: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <Label>Tax</Label>
                                    <Input
                                        data-testid="edit-tax"
                                        type="number"
                                        step="0.01"
                                        value={draft.tax}
                                        onChange={(e) => setDraft((d) => ({ ...d, tax: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <Label>Total</Label>
                                    <Input
                                        data-testid="edit-total"
                                        type="number"
                                        step="0.01"
                                        value={draft.total}
                                        onChange={(e) => setDraft((d) => ({ ...d, total: e.target.value }))}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="text-xs text-muted-foreground mb-2">Original bill</div>
                        {bill.thumbnail ? (
                            <img
                                data-testid="bill-detail-thumbnail"
                                src={bill.thumbnail}
                                alt="Bill preview"
                                className="w-full rounded-lg border border-border object-contain bg-muted/40"
                            />
                        ) : (
                            <div className="h-40 rounded-lg border border-dashed border-border text-xs text-muted-foreground flex items-center justify-center">
                                No preview
                            </div>
                        )}
                        {canDownload && !editing && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onDownload}
                                disabled={downloading}
                                data-testid="bill-detail-download-side"
                                className="gap-2 mt-3 w-full"
                            >
                                <Download size={14} /> Download original
                            </Button>
                        )}
                    </div>
                </div>
            </Card>

            {/* Quick-fix tax dialog */}
            <Dialog open={fixTaxOpen} onOpenChange={(v) => { if (!v && !fixTaxSaving) setFixTaxOpen(false); }}>
                <DialogContent data-testid="fix-tax-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Fix tax</DialogTitle>
                        <DialogDescription>
                            Enter the correct CGST/SGST (intra-state) OR IGST (inter-state) from the original bill.
                            The total will recompute automatically.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-3">
                            <div>
                                <Label htmlFor="fix-tax-cgst" className="text-xs mb-1.5 block">CGST amount (₹)</Label>
                                <Input
                                    id="fix-tax-cgst"
                                    data-testid="fix-tax-cgst"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={fixTaxDraft.cgst}
                                    onChange={(e) => setFixTaxDraft((d) => ({ ...d, cgst: e.target.value }))}
                                    placeholder="e.g. 118.90"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <Label htmlFor="fix-tax-sgst" className="text-xs mb-1.5 block">SGST/UTGST amount (₹)</Label>
                                <Input
                                    id="fix-tax-sgst"
                                    data-testid="fix-tax-sgst"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={fixTaxDraft.sgst}
                                    onChange={(e) => setFixTaxDraft((d) => ({ ...d, sgst: e.target.value }))}
                                    placeholder="e.g. 118.90"
                                />
                            </div>
                            <div>
                                <Label htmlFor="fix-tax-igst" className="text-xs mb-1.5 block">IGST amount (₹)</Label>
                                <Input
                                    id="fix-tax-igst"
                                    data-testid="fix-tax-igst"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={fixTaxDraft.igst}
                                    onChange={(e) => setFixTaxDraft((d) => ({ ...d, igst: e.target.value }))}
                                    placeholder="Leave blank if intra-state"
                                />
                            </div>
                        </div>
                        {/* Live preview of the resulting math */}
                        {(() => {
                            const c = Number(fixTaxDraft.cgst) || 0;
                            const s = Number(fixTaxDraft.sgst) || 0;
                            const ig = Number(fixTaxDraft.igst) || 0;
                            const t = c + s + ig;
                            const sub = Number(bill?.subtotal) || 0;
                            const total = Number(bill?.total) || 0;
                            const residual = total - sub - t;
                            return (
                                <div className="rounded-md border border-border/60 bg-muted/40 p-2.5 text-xs font-mono space-y-0.5">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(sub, bill?.currency)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Tax (this input)</span><span>{fmtMoney(t, bill?.currency)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Round-off after fix</span>
                                        <span className={Math.abs(residual) > 5 ? 'text-red-600 font-medium' : 'text-emerald-600'}>
                                            {residual >= 0 ? '+' : ''}{fmtMoney(residual, bill?.currency)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between border-t border-border/40 pt-0.5 mt-0.5">
                                        <span className="text-muted-foreground">Bill total</span><span>{fmtMoney(total, bill?.currency)}</span>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setFixTaxOpen(false)}
                            disabled={fixTaxSaving}
                            data-testid="fix-tax-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={saveFixTax}
                            disabled={fixTaxSaving}
                            data-testid="fix-tax-save"
                        >
                            {fixTaxSaving ? 'Saving…' : 'Save tax'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Quick-fix DISCOUNT dialog (Sprint 2026-07). Twin of fix-tax
                for bills where OCR missed a "Less: Discount ₹X" or a
                "Cash Disc X%" line. Enter either the absolute amount OR
                the percent — the app will recompute the totals. */}
            <Dialog open={fixDiscountOpen} onOpenChange={(v) => { if (!v && !fixDiscountSaving) setFixDiscountOpen(false); }}>
                <DialogContent data-testid="fix-discount-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Fix discount</DialogTitle>
                        <DialogDescription>
                            Enter the &ldquo;Less: Discount&rdquo; / &ldquo;Cash Disc&rdquo; value the OCR missed.
                            Provide EITHER a flat ₹ amount OR a percentage — not both.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label htmlFor="fix-disc-amount" className="text-xs mb-1.5 block">Discount amount (₹)</Label>
                            <Input
                                id="fix-disc-amount"
                                data-testid="fix-discount-amount"
                                type="number"
                                step="0.01"
                                min="0"
                                value={fixDiscountDraft.amount}
                                onChange={(e) => setFixDiscountDraft((d) => ({ ...d, amount: e.target.value, percent: e.target.value ? '' : d.percent }))}
                                placeholder="e.g. 279.35"
                                autoFocus
                            />
                        </div>
                        <div className="text-center text-[10px] uppercase tracking-widest text-muted-foreground">or</div>
                        <div>
                            <Label htmlFor="fix-disc-percent" className="text-xs mb-1.5 block">Discount percent (%)</Label>
                            <Input
                                id="fix-disc-percent"
                                data-testid="fix-discount-percent"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={fixDiscountDraft.percent}
                                onChange={(e) => setFixDiscountDraft((d) => ({ ...d, percent: e.target.value, amount: e.target.value ? '' : d.amount }))}
                                placeholder="e.g. 5"
                            />
                        </div>
                        {/* Live preview of the new totals under the current input. */}
                        {(() => {
                            const amt = Number(fixDiscountDraft.amount) || 0;
                            const pct = Number(fixDiscountDraft.percent) || 0;
                            const sub = Number(bill?.subtotal) || 0;
                            const total = Number(bill?.total) || 0;
                            const tax = Number(bill?.tax) || 0;
                            const impliedDiscount = amt || (pct > 0 ? +(sub * pct / 100).toFixed(2) : 0);
                            const residual = total - (sub + tax - impliedDiscount);
                            return (
                                <div className="rounded-md border border-border/60 bg-muted/40 p-2.5 text-xs font-mono space-y-0.5">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(sub, bill?.currency)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>{fmtMoney(tax, bill?.currency)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Discount (this input)</span><span className="text-[hsl(var(--price-decrease-fg))]">-{fmtMoney(impliedDiscount, bill?.currency)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Round-off after fix</span>
                                        <span className={Math.abs(residual) > 5 ? 'text-red-600 font-medium' : 'text-emerald-600'}>
                                            {residual >= 0 ? '+' : ''}{fmtMoney(residual, bill?.currency)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between border-t border-border/40 pt-0.5 mt-0.5">
                                        <span className="text-muted-foreground">Bill total</span><span>{fmtMoney(total, bill?.currency)}</span>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setFixDiscountOpen(false)}
                            disabled={fixDiscountSaving}
                            data-testid="fix-discount-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={saveFixDiscount}
                            disabled={fixDiscountSaving}
                            data-testid="fix-discount-save"
                        >
                            {fixDiscountSaving ? 'Saving…' : 'Save discount'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
